import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader, Panel, StatTile, EmptyState, BarChart, LineChart, SegmentedControl } from '../../ds/index'
import { useApi } from '../../lib/useApi'
import { money, num } from '../../lib/format'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'
import { Term } from '../../components/Term'
import { TierBadge } from './ClientLeads'

type Range = '3' | '6' | '12' | 'all'
type Spending = {
  range: number | 'all'; months: number
  totals: {
    leads: number; paid_cents: number; list_cents: number; credit_cents: number; bill_cents: number
    saved_cents: number; average_cents: number; deposited_cents: number; refunded: number; grading: number
  }
  series: { month: string; short: string; label: string; leads: number; credit_cents: number; bill_cents: number; paid_cents: number; list_cents: number }[]
  tiers: { key: string; name: string; leads: number; paid_cents: number }[]
  areas: { name: string; leads: number; paid_cents: number }[]
  outcomes: { contacted: number; not_yet: number; unanswered: number }
}

const RANGES: { value: Range; label: string }[] = [
  { value: '3', label: '3 months' }, { value: '6', label: '6 months' }, { value: '12', label: '12 months' }, { value: 'all', label: 'All time' },
]
const readRange = (): Range => { try { const v = localStorage.getItem('hp-spend-range'); return RANGES.some((r) => r.value === v) ? (v as Range) : '6' } catch { return '6' } }
const dollars = (v: number | string) => money(Number(v), { compact: true }).replace('.00', '')
const plural = (n: number, w: string) => `${num(n)} ${w}${n === 1 ? '' : 's'}`

/** What the client spent and what they got for it, as pictures first and numbers second. */
export default function ClientSpending() {
  const [range, setRangeState] = useState<Range>(readRange)
  const setRange = (r: Range) => { setRangeState(r); try { localStorage.setItem('hp-spend-range', r) } catch { /* private window */ } }
  const { data, error } = useApi<Spending>(`/api/client/spending?range=${range}`)
  const period = range === 'all' ? 'all time' : `the last ${range} months`

  return (
    <>
      <PageHeader title="Spending" subtitle="What you spent on leads and what you got for it." />
      <div className="range-row">
        <SegmentedControl<Range> segments={RANGES} value={range} onChange={setRange} />
        <Link to="/portal/leads?month=all">See every lead</Link>
      </div>
      {error && <ErrorBlock error={error} />}
      {!data ? (!error && <LoadingBlock />) : data.totals.leads === 0 ? (
        <Panel><EmptyState title={`No leads in ${period}`} body="Once leads come in, this page shows what each month cost and what you got." /></Panel>
      ) : <Body data={data} period={period} />}
    </>
  )
}

function Body({ data, period }: { data: Spending; period: string }) {
  const t = data.totals
  const creditPct = t.paid_cents ? (t.credit_cents / t.paid_cents) * 100 : 0
  const answered = data.outcomes.contacted + data.outcomes.not_yet
  const series = data.series.map((s) => ({ month: data.months > 6 ? s.label : s.short, credit: s.credit_cents, bill: s.bill_cents, leads: s.leads, avg: s.leads ? Math.round(s.paid_cents / s.leads) : (null as unknown as number) }))
  // Leads sold before tiers had no grade; show them as their own line so the bars add up.
  const tiered = data.tiers.reduce((n, x) => n + x.leads, 0)
  const untiered = { leads: t.leads - tiered, paid_cents: t.paid_cents - data.tiers.reduce((n, x) => n + x.paid_cents, 0) }
  const maxTier = Math.max(1, untiered.leads, ...data.tiers.map((x) => x.leads))
  const maxArea = Math.max(1, ...data.areas.map((x) => x.leads))
  const outcomes = [
    { label: 'Reached out', n: data.outcomes.contacted },
    { label: 'Not yet', n: data.outcomes.not_yet },
    { label: 'No answer yet', n: data.outcomes.unanswered },
  ]
  const maxOut = Math.max(1, ...outcomes.map((o) => o.n))

  return (
    <>
      <div className="spend-hero">
        <section className="hero-card is-dark" aria-label="Spent">
          <span className="hero-kicker">Spent in {period}</span>
          <span className="hero-figure">{money(t.paid_cents)}</span>
          <span className="hero-sub muted">on {plural(t.leads, 'lead')}, {money(t.average_cents)} each on average</span>
          <div className="spend-split" aria-label="How it was paid">
            <div className="spend-split-bar" role="img" aria-label={`${money(t.credit_cents)} from credit, ${money(t.bill_cents)} on the bill`}>
              {t.credit_cents > 0 && <span style={{ width: `${creditPct}%`, background: 'var(--viz-1)' }} />}
              {t.bill_cents > 0 && <span style={{ width: `${100 - creditPct}%`, background: 'var(--viz-2)' }} />}
            </div>
            <div className="spend-split-key">
              <span><i className="spend-swatch" style={{ background: 'var(--viz-1)' }} /><Term tip="credit">From credit</Term> {money(t.credit_cents)}</span>
              <span><i className="spend-swatch" style={{ background: 'var(--viz-2)' }} /><Term tip="bill">On your bill</Term> {money(t.bill_cents)}</span>
            </div>
          </div>
        </section>
        <Panel>
          <BarChart data={series} xKey="month" stacked height={240} format={dollars} showTable title="Spent by month"
                    series={[{ key: 'credit', label: 'From credit' }, { key: 'bill', label: 'On your bill' }]} />
        </Panel>
      </div>

      <div className="spend-tiles">
        <StatTile label="Leads received" value={num(t.leads)} meta={t.grading ? `${t.grading} more being graded` : `in ${period}`} />
        <StatTile label="Average per lead" value={money(t.average_cents)} meta="what each lead cost you" />
        <StatTile label="Saved by prepaying" value={money(t.saved_cents)} meta={`against ${money(t.list_cents)} at normal prices`} />
        <StatTile label="Money added" value={money(t.deposited_cents)} meta={t.refunded ? `${plural(t.refunded, 'lead')} refunded` : `in ${period}`} />
      </div>

      <div className="two-col">
        <Panel>
          <BarChart data={series} xKey="month" series={[{ key: 'leads', label: 'Leads' }]} title="Leads by month" height={220} showTable integer />
        </Panel>
        <Panel>
          <LineChart data={series} xKey="month" series={[{ key: 'avg', label: 'Average per lead' }]} title="Average cost per lead" dots subtitle="Months with no leads are left blank" height={220} format={(v) => (v == null ? 'No leads' : dollars(v))} showTable />
        </Panel>
      </div>

      <div style={{ height: 'var(--s-6)' }} />
      <div className="two-col">
        <Panel>
          <div className="panel-header"><h2 className="panel-title"><Term tip="tier">What you got, by tier</Term></h2></div>
          <div className="tier-bars">
            {data.tiers.map((x) => (
              <div key={x.key} className="tier-bar">
                <TierBadge name={x.name} tierKey={x.key} />
                <div className="tier-bar-track" role="img" aria-label={`${x.name}: ${x.leads} leads`}><span style={{ width: `${(x.leads / maxTier) * 100}%` }} /></div>
                <span className="tier-bar-value"><strong>{x.leads}</strong> <span className="muted">{money(x.paid_cents)}</span></span>
              </div>
            ))}
            {untiered.leads > 0 && (
              <div className="tier-bar">
                <span className="muted" style={{ fontSize: 13 }}>No tier</span>
                <div className="tier-bar-track" role="img" aria-label={`No tier: ${untiered.leads} leads`}><span style={{ width: `${(untiered.leads / maxTier) * 100}%` }} /></div>
                <span className="tier-bar-value"><strong>{untiered.leads}</strong> <span className="muted">{money(untiered.paid_cents)}</span></span>
              </div>
            )}
          </div>
        </Panel>
        <Panel>
          <div className="panel-header">
            <h2 className="panel-title">Did they reach out?</h2>
            <span className="muted">{t.leads ? `${Math.round((data.outcomes.contacted / t.leads) * 100)}% did` : ''}</span>
          </div>
          <div className="tier-bars outcome-bars">
            {outcomes.map((o) => (
              <div key={o.label} className="tier-bar">
                <span style={{ fontSize: 14 }}>{o.label}</span>
                <div className="tier-bar-track" role="img" aria-label={`${o.label}: ${o.n}`}><span style={{ width: `${(o.n / maxOut) * 100}%` }} /></div>
                <span className="tier-bar-value"><strong>{o.n}</strong></span>
              </div>
            ))}
          </div>
          {data.outcomes.unanswered > 0 && (
            <p className="muted" style={{ marginBottom: 0, fontSize: 13 }}>
              {answered === 0 ? 'Tell us which leads reached out: ' : 'Answer the rest on '}<Link to="/portal/leads?month=all">your leads</Link>. It helps send you more of the good ones.
            </p>
          )}
        </Panel>
      </div>

      <div style={{ height: 'var(--s-6)' }} />
      <Panel>
        <div className="panel-header"><h2 className="panel-title">Where your leads came from</h2></div>
        <div className="tier-bars area-bars">
          {data.areas.map((a) => (
            <div key={a.name} className="tier-bar">
              <span title={a.name}>{a.name}</span>
              <div className="tier-bar-track" role="img" aria-label={`${a.name}: ${a.leads} leads`}><span style={{ width: `${(a.leads / maxArea) * 100}%` }} /></div>
              <span className="tier-bar-value"><strong>{a.leads}</strong> <span className="muted">{money(a.paid_cents)}</span></span>
            </div>
          ))}
        </div>
      </Panel>
    </>
  )
}
